// Typed observer bus (00-architecture.md §4): the sim emits, the view
// subscribes. Emission is synchronous; game systems never subscribe.

// Type-only import: erased at build time, so the blocks <-> events cycle
// never exists at runtime.
import type { BlockState, Cell } from "./blocks";
import type { BugId, BugKind, BugState } from "./bugs";
import type { SpawnerId, SpawnerState } from "./spawners";
import type { PowerUpId, PowerUpState } from "./powerups";
import type { WalkerId } from "./occupancy";
import type { LevelDef } from "./defs";

/**
 * Every event payload the simulation can emit. Later plans extend this
 * interface; each payload is a plain readonly object.
 */
export interface GameEvents {
  blockChanged: {
    readonly x: number;
    readonly y: number;
    readonly z: number;
    readonly state: BlockState;
  };
  bugSpawned: {
    readonly id: BugId;
    readonly kind: BugKind;
  };
  bugStateChanged: {
    readonly id: BugId;
    readonly state: BugState["kind"];
  };
  bugRemoved: {
    readonly id: BugId;
  };
  spawnerAdded: {
    readonly id: SpawnerId;
  };
  spawnerStateChanged: {
    readonly id: SpawnerId;
    readonly state: SpawnerState["kind"];
  };
  /** A tap the bug survived — the fat bug's first two (`OnBugHitChanged`). */
  bugHit: {
    readonly id: BugId;
    readonly hitsTaken: number;
  };
  /**
   * Points awarded. Score is `sum(points) x product(multipliers)` over a frame
   * in the original (`Cube.AddScore`), so both halves travel together; the HUD
   * that consumes them is a later plan.
   */
  scored: {
    readonly points: number;
    readonly multiplier: number;
  };
  /**
   * A spy's marked-block list changed — the original's
   * `OnBugMarkedBlocksChanged` (`BugsModel.cs:1370`, `:1880`, `:2806`). It fires
   * on every mark added, on the cash-in that clears them, and on the forfeit a
   * kill causes. The original draws a `FieldMarker` prefab per mark; nothing
   * consumes this yet (plan 14 emits it so the view can be added without the
   * sim changing again).
   */
  bugMarksChanged: {
    readonly id: BugId;
    readonly marks: readonly Cell[];
  };
  /** A repair refused because a walker — or a power-up — stands on the block. */
  repairBlocked: {
    readonly cell: Cell;
    readonly blockers: readonly (WalkerId | PowerUpId)[];
  };
  /** A repair chain that ended at length >= 2 (`BlockCombos.GetLastCombo`). */
  combo: {
    readonly cells: readonly Cell[];
    readonly length: number;
  };
  /** An apple removed because the new level wants fewer spawn points. */
  spawnerRemoved: {
    readonly id: SpawnerId;
  };
  /**
   * The clock crossed into a new level (`Cube.LevelChanged`). Fires once per
   * level, in order, even when a big time jump crosses several at once.
   */
  levelChanged: {
    readonly level: number;
    readonly def: LevelDef;
  };
  /**
   * The run was won — the `GAME_WON_AFTER_LEVEL` window expired. Fires **once**
   * and **nothing stops**: no state freezes, no bug is culled, the clock keeps
   * ticking. The shell that halts the sim is a later plan.
   */
  gameWon: {
    readonly level: number;
  };
  /**
   * The cube fell to `LOSE_HEALTHY_FRACTION` or below. Fires **once** and, like
   * `gameWon`, changes nothing about the simulation — a lost run simply keeps
   * playing until someone builds the shell that listens.
   */
  gameLost: {
    readonly healthyFraction: number;
  };
  /** A power-up appeared on the surface (plan 16). */
  powerUpSpawned: {
    readonly id: PowerUpId;
    readonly slot: number;
  };
  powerUpStateChanged: {
    readonly id: PowerUpId;
    readonly state: PowerUpState["kind"];
  };
  /** The `pick` or `die` clip elapsed and the power-up is gone. */
  powerUpRemoved: {
    readonly id: PowerUpId;
  };
  /**
   * The player collected a power-up — what a HUD and the scroller texts will
   * want (the slot indexes both `POWERUP_DEFS` and the `ScrollerTexts` in
   * `unity-overview.md` §5). Nothing consumes it yet, like `gameWon`; the
   * points it carries are also emitted as `scored` with `multiplier: 1`.
   */
  powerUpTaken: {
    readonly id: PowerUpId;
    readonly slot: number;
    readonly points: number;
  };
}

type Listener<K extends keyof GameEvents> = (e: GameEvents[K]) => void;

export class EventBus {
  // The one place a loose type is acceptable: listeners of different events
  // have incompatible signatures, so the map is untyped inside and the
  // public API re-establishes the typing. Nothing loose leaks out.
  private readonly listeners = new Map<string, Set<(e: never) => void>>();

  /** Subscribe to an event; returns an unsubscribe function. */
  on<K extends keyof GameEvents>(key: K, fn: Listener<K>): () => void {
    let set = this.listeners.get(key);
    if (!set) {
      set = new Set();
      this.listeners.set(key, set);
    }
    const entry = fn as (e: never) => void;
    set.add(entry);
    return () => {
      set.delete(entry);
    };
  }

  /** Notify every current listener, in subscription order. */
  emit<K extends keyof GameEvents>(key: K, e: GameEvents[K]): void {
    const set = this.listeners.get(key);
    if (!set) return;
    // Copy first so a listener unsubscribing during dispatch is safe.
    for (const fn of [...set]) (fn as Listener<K>)(e);
  }
}
